import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Box, Card, Typography, Divider, useTheme, useColorScheme } from "@mui/joy";
import { useProject } from "../../../../hooks/query";

function getMonthLabel(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export default function Analytics() {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const { mode, setMode } = useColorScheme();

    const { useList } = useProject();
    const listProjectSvc = useList();

    useEffect(() => {
        listProjectSvc.mutateAsync().catch(console.error);
    }, []);

    useEffect(() => {
        if (!chartRef.current || !listProjectSvc.data) return;

        const projects = listProjectSvc.data;
        const currentYear = new Date().getFullYear();

        // Filter data
        const createdThisYear = projects.filter(
            (p: any) => p.CreatedAt && new Date(p.CreatedAt).getFullYear() === currentYear,
        );
        const completedThisYear = projects.filter((p: any) => {
            // Check if completed this year. Fallback to UpdateDate if ActualEndDate is missing.
            const dateStr = p.ActualEndDate || p.UpdatedAt || p.CreatedAt;
            return p.Status === "COMPLETED" && new Date(dateStr).getFullYear() === currentYear;
        });

        const monthMap: Record<string, { created: number; completed: number }> = {};

        for (let i = 0; i < 12; i++) {
            const month = `${currentYear}-${String(i + 1).padStart(2, "0")}`;
            monthMap[month] = { created: 0, completed: 0 };
        }

        createdThisYear.forEach((p: any) => {
            const key = getMonthLabel(p.CreatedAt);
            if (monthMap[key]) monthMap[key].created += 1;
        });

        completedThisYear.forEach((p: any) => {
            const key = getMonthLabel(p.ActualEndDate || p.UpdatedAt || p.CreatedAt);
            if (monthMap[key]) monthMap[key].completed += 1;
        });

        const months = Object.keys(monthMap);
        const createdData = months.map((m) => monthMap[m].created);
        const completedData = months.map((m) => monthMap[m].completed);

        const chart = echarts.init(chartRef.current, mode === "dark" ? "dark" : "light");

        const option = {
            backgroundColor: "transparent",
            textStyle: {
                fontFamily: "Inter, sans-serif",
            },
            tooltip: {
                trigger: "axis",
                backgroundColor: mode === "dark" ? "#1f2937" : "#ffffff",
                borderColor: mode === "dark" ? "#374151" : "#e5e7eb",
                textStyle: { color: mode === "dark" ? "#f9fafb" : "#111827" },
                padding: [12, 16],
                borderRadius: 8,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            },
            legend: {
                data: ["Projects Created", "Projects Completed"],
                orient: "horizontal",
                right: "0",
                top: "0",
                itemGap: 24,
                icon: "circle",
                textStyle: {
                    fontWeight: 500,
                    fontSize: 13,
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                },
            },
            grid: {
                left: "2%",
                right: "4%",
                bottom: 45,
                top: 45,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                boundaryGap: false,
                axisLabel: {
                    fontSize: 12,
                    fontWeight: 500,
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    margin: 16,
                },
                axisLine: {
                    lineStyle: { color: mode === "dark" ? "#374151" : "#e5e7eb" },
                },
                axisTick: { show: false },
                splitLine: { show: false },
                data: months.map((m) => {
                    const monthNum = parseInt(m.split("-")[1], 10);
                    return new Date(currentYear, monthNum - 1).toLocaleString("default", { month: "short" });
                }),
            },
            yAxis: {
                type: "value",
                axisLabel: {
                    fontSize: 12,
                    fontWeight: 500,
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    margin: 16,
                },
                splitLine: {
                    lineStyle: {
                        type: "dashed",
                        color: mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    },
                },
            },
            series: [
                {
                    name: "Projects Created",
                    type: "line",
                    data: createdData,
                    itemStyle: { color: "#0f764a" },
                    lineStyle: { width: 3, color: "#0f764a" },
                    symbol: "circle",
                    symbolSize: 8,
                    smooth: true,
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: "rgba(15, 118, 74, 0.4)" },
                            { offset: 1, color: "rgba(15, 118, 74, 0.0)" },
                        ]),
                    },
                },
                {
                    name: "Projects Completed",
                    type: "line",
                    data: completedData,
                    itemStyle: { color: "#eab308" },
                    lineStyle: { width: 3, color: "#eab308" },
                    symbol: "circle",
                    symbolSize: 8,
                    smooth: true,
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: "rgba(234, 179, 8, 0.4)" },
                            { offset: 1, color: "rgba(234, 179, 8, 0.0)" },
                        ]),
                    },
                },
            ],
        };

        chart.setOption(option);

        const handleResize = () => chart.resize();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            chart.dispose();
        };
    }, [listProjectSvc.data, mode]);

    return (
        <Card
            variant="plain"
            sx={{
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                height: "100%",
                boxShadow: "sm",
                borderRadius: "xl",
                bgcolor: "background.surface",
                border: "none",
            }}
        >
            <Typography level="title-lg" sx={{ fontWeight: 700, mb: 1 }}>
                Project Pipeline Flow
            </Typography>

            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: 280,
                }}
            />
        </Card>
    );
}
